import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { FormValidatorService } from '../../services/form-validator.service';
import { WizardFormData } from './wizard-form.data';
import { AuthenticationService } from 'src/app/authentication/services/authentication.service';
import { ContainerType } from '../../models/container.types';

@Component({
    selector: 'app-wizard-form',
    templateUrl: './wizard-form.component.html',
    styleUrls: ['./wizard-form.component.scss'],
    standalone: false,
})
export class WizardFormComponent implements OnInit, OnDestroy, AfterViewInit {
    form: FormGroup;

    formControlName: string = 'name';
    isInOrganizationContext: boolean = false;
    organizationGroups: { id: string; name: string }[] = [];

    @ViewChild('inputName') inputName: ElementRef;

    @Input() hasSubmitFailed: Subject<boolean>;

    @Input() type: ContainerType;

    @Output() newData = new EventEmitter<WizardFormData>();

    submitButtonTxt: string = this.translate.instant(
        'imgDb.wizard.wizardSlideTS.hereWeGo'
    );

    sub: Subscription;

    @Output() backClicked = new EventEmitter<void>();

    constructor(
        private formValidatorService: FormValidatorService,
        private translate: TranslateService,
        private authenticationService: AuthenticationService
    ) {}

    ngOnInit() {
        let control = this.formValidatorService.getControlByType('name');
        this.form = new FormGroup({
            [this.formControlName]: control,
            groupId: new FormControl(null),
        });
        this.initializeOrganizationGroups();

        this.sub = this.hasSubmitFailed.subscribe((hasSubmitFailed) => {
            if (hasSubmitFailed) {
                this.submitButtonTxt = this.translate.instant(
                    'imgDb.wizard.wizardSlideTS.hereWeGo'
                );
                this.resetHasSubmitFailed();
            }
        });
    }

    ngAfterViewInit(): void {
        this.inputName.nativeElement.focus();
    }

    onSubmit() {
        if (this.nameControl.valid) {
            this.submitButtonTxt = this.translate.instant(
                'imgDb.wizard.wizardSlideTS.pleaseWait'
            );

            const formData: WizardFormData = {
                name: this.inputValue,
                description: '',
                groupId: this.groupIdControl.value,
            };

            this.newData.emit(formData);
        }
    }

    get nameControl(): FormControl {
        return this.form.get(this.formControlName) as FormControl;
    }

    get inputValue(): string {
        return this.nameControl.value;
    }

    get groupIdControl(): FormControl {
        return this.form.get('groupId') as FormControl;
    }

    onBlur() {
        if (this.inputValue !== null) {
            this.nameControl.setValue(this.inputValue.trim());
        }
    }

    resetHasSubmitFailed() {
        this.hasSubmitFailed.next(false);
    }

    onBackClick() {
        this.backClicked.emit();
        this.form.reset({ [this.formControlName]: '', groupId: null });
    }

    private initializeOrganizationGroups(): void {
        const userAuth = this.authenticationService.currentUserValue;
        const activeOrganization = userAuth?.activeOrganization;
        if (!activeOrganization) {
            return;
        }

        this.isInOrganizationContext = true;
        const groups =
            userAuth.organizationProperties?.[activeOrganization]?.groups ?? {};
        this.organizationGroups = Object.entries(groups).map(([id, name]) => ({
            id,
            name,
        }));
    }

    get translationKeyPrefix() {
        return ContainerType.IMAGE_DB === this.type
            ? 'imgDb.wizard'
            : 'project.components';
    }

    ngOnDestroy() {
        this.sub.unsubscribe();
    }
}
